import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react";

const QualityInspection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openJobs, setOpenJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [inspectionPlan, setInspectionPlan] = useState([]);
  const [inspectionType, setInspectionType] = useState("First");
  const [sampleSize, setSampleSize] = useState(2);
  const [readings, setReadings] = useState({});   // { s_no: [val, val, ...] }
  const [toggles, setToggles] = useState({});     // { s_no: 'PASS'|'FAIL'|'OK'|'NOT_OK' }
  const [dimRemarks, setDimRemarks] = useState({});
  const [overallRemarks, setOverallRemarks] = useState("");
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/production/open-jobs")
      .then((r) => {
        setOpenJobs(r.data);
        // Auto-select if `?job=<jobId>` was passed (from Inspection Queue).
        const wantId = searchParams.get("job");
        if (wantId) {
          const j = (r.data || []).find((x) => x.job_id === wantId);
          if (j) handleJobSelect(j);
        }
      })
      .catch(() => {});
  }, []);

  const handleJobSelect = async (job) => {
    setSelectedJob(job);
    setLoadingPlan(true);
    setError("");
    try {
      const [planRes, typeRes] = await Promise.all([
        api.get(`/inspection-plan?tool_no=${job.tool_no}`),
        api.get(`/inspection/check-type?tool_no=${job.tool_no}&job_id=${job.job_id}`),
      ]);
      setInspectionPlan(planRes.data);
      setInspectionType(typeRes.data.inspection_type);
      setSampleSize(typeRes.data.sample_size);

      // Initialize state
      const initReadings = {};
      const initToggles = {};
      for (const dim of planRes.data) {
        const count = dim.check_type === "Measured"
          ? (dim.multi_point_count > 1 ? dim.multi_point_count : typeRes.data.sample_size)
          : 1;
        if (dim.check_type === "Measured") {
          initReadings[dim.s_no] = Array(count).fill("");
        } else {
          initToggles[dim.s_no] = null;
        }
      }
      setReadings(initReadings);
      setToggles(initToggles);
      setDimRemarks({});
    } catch (err) {
      setError("Failed to load inspection plan");
    } finally {
      setLoadingPlan(false);
    }
  };

  const setReading = (sNo, idx, val) => {
    setReadings((prev) => {
      const arr = [...(prev[sNo] || [])];
      arr[idx] = val;
      return { ...prev, [sNo]: arr };
    });
  };

  const getDimResult = (dim) => {
    if (dim.check_type === "Measured") {
      const vals = readings[dim.s_no] || [];
      if (vals.some((v) => v === "")) return null;
      const nums = vals.map(Number);
      if (dim.lsl !== null && dim.usl !== null) {
        return nums.every((v) => v >= dim.lsl && v <= dim.usl) ? "PASS" : "FAIL";
      }
      return "PASS";
    }
    const toggle = toggles[dim.s_no];
    if (toggle === null || toggle === undefined) return null;
    return (toggle === "PASS" || toggle === "OK") ? "PASS" : "FAIL";
  };

  const allFilled = inspectionPlan.every((dim) => getDimResult(dim) !== null);

  const overallResult = allFilled
    ? (inspectionPlan.every((dim) => getDimResult(dim) === "PASS") ? "PASS" : "FAIL")
    : null;

  const handleSubmit = async () => {
    if (!allFilled) { setError("Please fill all dimensions"); return; }
    setSubmitting(true);
    setError("");
    try {
      const dimensions = inspectionPlan.map((dim) => ({
        s_no: dim.s_no,
        dimension_description: dim.dimension_description,
        check_type: dim.check_type,
        lsl: dim.lsl,
        usl: dim.usl,
        instrument: dim.instrument,
        readings: dim.check_type === "Measured" ? readings[dim.s_no] : null,
        toggle_result: dim.check_type !== "Measured" ? toggles[dim.s_no] : null,
        result: getDimResult(dim),
        remarks: dimRemarks[dim.s_no] || "",
      }));

      const res = await api.post("/inspection", {
        tool_no: selectedJob.tool_no,
        job_id: selectedJob.job_id,
        overall_remarks: overallRemarks,
        dimensions,
      });
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header title="Inspection" showBack backPath="/home" />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${success.overall_result === "PASS" ? "bg-green-100" : "bg-red-100"}`}>
            {success.overall_result === "PASS"
              ? <CheckCircle2 size={48} className="text-[#15803D]" />
              : <XCircle size={48} className="text-[#DC2626]" />}
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">
            Inspection {success.overall_result === "PASS" ? "PASSED" : "FAILED"}
          </h2>
          <p className="text-slate-500 mb-4">{success.inspection_type} Inspection — {success.inspection_id}</p>
          {success.overall_result === "FAIL" && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm mb-4">
              Supervisor and operator have been notified of the failure.
            </div>
          )}
          <button data-testid="inspection-done-btn" onClick={() => navigate("/home")}
            className="w-full h-12 bg-[#1A3560] text-white font-bold rounded-xl active:scale-[0.98] transition-all">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Select Job
  if (!selectedJob) {
    return (
      <div className="min-h-screen bg-slate-50 pb-4">
        <Header title="Quality Inspection" showBack backPath="/home" />
        <div className="max-w-md mx-auto px-4 py-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
            Select Running Job / चालू कार्य चुनें
          </p>
          {openJobs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400">
              <p className="font-medium">No open production jobs</p>
              <p className="text-sm mt-1">Start a production job to inspect</p>
            </div>
          ) : (
            <div className="space-y-3">
              {openJobs.map((job) => (
                <button
                  key={job.job_id}
                  data-testid={`inspect-job-${job.job_id}`}
                  onClick={() => handleJobSelect(job)}
                  className="w-full bg-white rounded-2xl border border-slate-200 p-4 text-left hover:shadow-md active:scale-[0.98] transition-all"
                >
                  <p className="font-extrabold text-[#1A3560]">{job.job_id}</p>
                  <p className="text-sm text-slate-600 mt-0.5">T{job.tool_no} — {job.part_name}</p>
                  <p className="text-xs text-slate-400 mt-1">{job.machine_code} | {job.operator_name} | Batch: {job.full_batch_code}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step 2: Inspection form
  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <Header title="Quality Inspection" showBack backPath="/inspection" />

      <div className="max-w-md mx-auto px-4 py-4">
        {/* Job & Inspection info */}
        <div className="bg-[#1A3560] rounded-2xl p-4 text-white mb-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-extrabold text-base">{selectedJob.part_name}</p>
              <p className="text-white/60 text-xs mt-0.5">T{selectedJob.tool_no} | {selectedJob.machine_code} | {selectedJob.operator_name}</p>
            </div>
            <div className={`px-3 py-1.5 rounded-xl text-xs font-extrabold ${
              inspectionType === "First" ? "bg-[#D97706] text-white" : "bg-white/20 text-white"
            }`}>
              {inspectionType.toUpperCase()}
            </div>
          </div>
          <div className="mt-3 flex gap-3 text-xs">
            <span className="bg-white/10 rounded-lg px-2 py-1">Sample Size: <b>{sampleSize}</b></span>
            <span className="bg-white/10 rounded-lg px-2 py-1">Batch: <b>{selectedJob.full_batch_code}</b></span>
          </div>
        </div>

        {loadingPlan ? (
          <div className="text-center py-12 text-slate-400">Loading inspection plan...</div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
              Dimensions ({inspectionPlan.length}) — {allFilled ? `Overall: ` : "Fill all dimensions"}
              {allFilled && (
                <span className={`ml-1 font-extrabold ${overallResult === "PASS" ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                  {overallResult}
                </span>
              )}
            </p>

            {inspectionPlan.map((dim) => {
              const result = getDimResult(dim);
              return (
                <div
                  key={dim.s_no}
                  data-testid={`dim-${dim.s_no}`}
                  className={`bg-white rounded-2xl border-2 mb-3 p-4 transition-all ${
                    result === "PASS" ? "border-[#15803D]" : result === "FAIL" ? "border-[#DC2626]" : "border-slate-200"
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <span className="text-xs font-bold text-slate-400 mr-2">S{dim.s_no}</span>
                      <span className="font-bold text-sm text-slate-900">{dim.dimension_description}</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {dim.lsl !== null && <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">LSL: {dim.lsl}</span>}
                        {dim.usl !== null && <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">USL: {dim.usl}</span>}
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{dim.instrument}</span>
                      </div>
                    </div>
                    {result && (
                      <span className={`flex-shrink-0 text-xs font-extrabold px-3 py-1 rounded-xl ml-2 ${
                        result === "PASS" ? "bg-green-100 text-[#15803D]" : "bg-red-100 text-[#DC2626]"
                      }`}>
                        {result}
                      </span>
                    )}
                  </div>

                  {/* Measured inputs */}
                  {dim.check_type === "Measured" && (
                    <div className="grid grid-cols-2 gap-2">
                      {(readings[dim.s_no] || []).map((val, idx) => {
                        const num = parseFloat(val);
                        const isValid = !isNaN(num) && val !== "";
                        const pass = isValid && dim.lsl !== null && dim.usl !== null
                          ? num >= dim.lsl && num <= dim.usl
                          : true;
                        return (
                          <div key={idx}>
                            <label className="text-xs text-slate-400 font-medium">
                              {dim.multi_point_count > 1 ? `Pt ${idx + 1}` : `Pc ${idx + 1}`}
                            </label>
                            <input
                              data-testid={`reading-s${dim.s_no}-${idx}`}
                              type="number"
                              step="0.001"
                              value={val}
                              onChange={(e) => setReading(dim.s_no, idx, e.target.value)}
                              placeholder="0.00"
                              className={`w-full h-11 px-3 rounded-lg border-2 text-sm font-bold text-center focus:outline-none transition-colors ${
                                isValid
                                  ? pass ? "border-[#15803D] bg-green-50 text-[#15803D]" : "border-[#DC2626] bg-red-50 text-[#DC2626]"
                                  : "border-slate-300 bg-white text-slate-900"
                              }`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Visual toggle */}
                  {dim.check_type === "Visual" && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        data-testid={`dim-${dim.s_no}-pass`}
                        onClick={() => setToggles((t) => ({ ...t, [dim.s_no]: "PASS" }))}
                        className={`h-14 rounded-xl font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.97] ${
                          toggles[dim.s_no] === "PASS"
                            ? "bg-[#15803D] text-white" : "bg-green-50 text-[#15803D] border-2 border-[#15803D]"
                        }`}
                      >
                        <CheckCircle2 size={18} /> PASS
                      </button>
                      <button
                        data-testid={`dim-${dim.s_no}-fail`}
                        onClick={() => setToggles((t) => ({ ...t, [dim.s_no]: "FAIL" }))}
                        className={`h-14 rounded-xl font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.97] ${
                          toggles[dim.s_no] === "FAIL"
                            ? "bg-[#DC2626] text-white" : "bg-red-50 text-[#DC2626] border-2 border-[#DC2626]"
                        }`}
                      >
                        <XCircle size={18} /> FAIL
                      </button>
                    </div>
                  )}

                  {/* Attribute toggle */}
                  {dim.check_type === "Attribute" && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        data-testid={`dim-${dim.s_no}-ok`}
                        onClick={() => setToggles((t) => ({ ...t, [dim.s_no]: "OK" }))}
                        className={`h-14 rounded-xl font-bold transition-all active:scale-[0.97] ${
                          toggles[dim.s_no] === "OK"
                            ? "bg-[#15803D] text-white" : "bg-green-50 text-[#15803D] border-2 border-[#15803D]"
                        }`}
                      >
                        OK / स्वीकार्य
                      </button>
                      <button
                        data-testid={`dim-${dim.s_no}-nok`}
                        onClick={() => setToggles((t) => ({ ...t, [dim.s_no]: "NOT_OK" }))}
                        className={`h-14 rounded-xl font-bold transition-all active:scale-[0.97] ${
                          toggles[dim.s_no] === "NOT_OK"
                            ? "bg-[#DC2626] text-white" : "bg-red-50 text-[#DC2626] border-2 border-[#DC2626]"
                        }`}
                      >
                        NOT OK / अस्वीकार्य
                      </button>
                    </div>
                  )}

                  {dim.inspector_note && (
                    <p className="text-xs text-[#D97706] mt-2 italic">{dim.inspector_note}</p>
                  )}

                  <textarea
                    value={dimRemarks[dim.s_no] || ""}
                    onChange={(e) => setDimRemarks((r) => ({ ...r, [dim.s_no]: e.target.value }))}
                    placeholder="Remarks (optional)"
                    rows={1}
                    className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#1A3560] resize-none bg-slate-50"
                  />
                </div>
              );
            })}

            {/* Overall remarks */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4">
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                Overall Remarks
              </label>
              <textarea
                data-testid="overall-remarks"
                value={overallRemarks}
                onChange={(e) => setOverallRemarks(e.target.value)}
                placeholder="Overall inspection comments..."
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1A3560] resize-none"
              />
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm flex items-center gap-2">
                <AlertCircle size={16} /> {error}
              </div>
            )}
          </>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-md mx-auto">
          <button
            data-testid="inspection-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !allFilled}
            className={`w-full h-14 font-bold text-base rounded-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2 ${
              allFilled
                ? overallResult === "PASS"
                  ? "bg-[#15803D] text-white"
                  : "bg-[#DC2626] text-white"
                : "bg-slate-200 text-slate-400 cursor-not-allowed"
            }`}
          >
            {submitting
              ? <><div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Submitting...</>
              : `Submit Inspection${overallResult ? ` — ${overallResult}` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QualityInspection;
